// React-related imports
import { FC, useMemo, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row } from '@tanstack/react-table';

// Components
import { PaginatedTable } from '../../../../components/PaginatedTable';
import TinyMceModal from '../Modal/TinyMceModal/TinyMceModal';
import { DeleteConfirmationDialogue } from '../../../../components/modals/DeleteConfirmationDialogue';
import { EditCatalogTemplates } from '../EditCatalogTemplates';
import ConvertToAutoMappingModal from '../../../../components/elements/ConvertToAutoMappingModal';
import SvgIcon from '../../../../components/elements/SvgIcon';
import { Dropdown } from '@GxT-Team/gxt-components';

// API services
import {
  getCatalogs,
  getMappingSessions,
  useCatalog,
  useMappingSessionAPI,
} from '../../../../services/api';

// Types
import { CatalogEditValues, CatalogTableProps } from './CatalogTable.types';
import { DocumentTabs, MappingSessionRecord } from '../../../../types';
import { ItemType, MappingSession, PaginationResponse } from '../../../../services/api/types';
import { Catalog } from '../../../../services/api/catalog/catalog.types';
import { CatalogItemsHash } from '../../../upload';

// Utility functions
import { generateColumnsByTab } from './utils/catalogColumns';
import { AddSourcesPreviousAction } from '../../util/constants';
import { getDropdownOptions } from '../../../../helpers/utils';
import { twMerge } from 'tailwind-merge';

// Other utility components
import generateMenuItems from './utils/menuItems';
import { createOptionValues, disableConvert } from './utils/helper';

// Hooks
import useModal from '../../../../hooks/useModal';
import CopyDocumentSlideOver from '../CopyDocumentSlideOver/CopyDocumentSlideOver';
import ViewSessionDataSlideOver from '../ViewSessionDataSlideOver/ViewSessionDataSlideOver';
import {
  MenuItemType,
  BaseItem,
  PaginatedTableProps,
} from '../../../../components/PaginatedTable/PaginatedTable.types';

type CatalogTableRow = MappingSessionRecord & Record<'id', string>;
type CatalogPreviewData = { id?: number; targetFileId?: number };

const CatalogTable: FC<CatalogTableProps> = ({
  activeTab,
  paginatedFilteredData,
  handleColumnSort,
  templateType,
  handleSelectAll,
  handleSelect,
  handlePageChange,
  handlePerPageChange,
  selectedRecords,
}) => {
  const [selectedId, setSelectedId] = useState<number>(0);
  const [isDocInteractive, setDocInteractive] = useState(true);
  const [selectedSession, setSelectedSession] = useState<Partial<MappingSessionRecord>>({});
  const [defaultFormValues, setDefaultFormValues] = useState<
    Record<DocumentTabs, CatalogEditValues>
  >({
    Automated: { templateName: '', version: 1.0, id: 0, ctdSections: [], materials: [] },
    Target: { id: 0, ctdSections: [], materials: [] },
    Source: {
      id: 0,
      materials: [],
      processIds: [],
      sites: [],
      dpImages: [],
      documentNum: '',
      version: 0,
    },
    'In-Progress': {
      title: '',
      project: [],
      ctdSections: [],
      id: 0,
    },
    'Pre-Authored': { templateName: '', version: 1.0, id: 0, ctdSections: [] },
    Completed: { templateName: '', version: 1.0, id: 0, ctdSections: [] },
    External: { id: 0, materials: [] },
  });

  const [selectedAutomatedDocsId, setSelectedAutomatedDocsId] = useState<number>(0);
  const [hasProcessingItems, setHasProcessingItems] = useState(false);

  const PROCESSING_POLL_INTERVAL_MS = 5000;

  useEffect(() => {
    setHasProcessingItems(false);
  }, [activeTab]);

  const handleDataFetched = useCallback(
    (data: PaginationResponse<Catalog>) => {
      if (activeTab === DocumentTabs.SOURCE) {
        setHasProcessingItems(
          data?.data?.some((item) => item.processingStatus === 'processing') ?? false,
        );
        return;
      }
      setHasProcessingItems(false);
    },
    [activeTab],
  );

  const navigate = useNavigate();

  const catalogEdit = useModal();
  const deleteModal = useModal();
  const viewSessionDataModal = useModal();
  const previewModal = useModal();
  const previewTargetAutomatedModal = useModal();
  const convertToAutomatedModal = useModal();
  const copyDocumentModal = useModal();

  const { deleteCatalog } = useCatalog();
  const { archiveSession } = useMappingSessionAPI();

  const sortedColumn = useMemo(
    () => ({
      column: paginatedFilteredData.sort?.column || '',
      order: paginatedFilteredData.sort?.type || 'DESC',
    }),
    [paginatedFilteredData.sort?.column, paginatedFilteredData.sort?.type],
  );

  const selectedTab =
    activeTab === DocumentTabs.TARGET ? (templateType as DocumentTabs) : activeTab;

  const activeTabColumns = generateColumnsByTab(
    activeTab === DocumentTabs.TARGET && templateType === 'Automated'
      ? DocumentTabs.AUTOMATED
      : activeTab,
    handleColumnSort,
    sortedColumn,
  );

  const closeDeleteModal = useCallback(() => {
    deleteModal.closeModal();
    setSelectedId(0);
  }, [deleteModal]);

  const handleCatalogDelete = useCallback(async () => {
    await deleteCatalog.mutateAsync([selectedId]);
    closeDeleteModal();
  }, [deleteCatalog, selectedId, closeDeleteModal]);

  const handleSessionDelete = useCallback(async () => {
    await archiveSession.mutateAsync([selectedId]);
    closeDeleteModal();
  }, [archiveSession, selectedId, closeDeleteModal]);

  const setPreviewDocId = useCallback((id: number) => {
    setSelectedId(id);
  }, []);

  const handlePreviewDocument = useCallback(
    (id: number, documentType: boolean) => {
      setPreviewDocId(id);
      previewModal.openModal();
      setDocInteractive(documentType);
    },
    [setPreviewDocId, previewModal],
  );

  const viewSessionData = useCallback(
    (row: Row<CatalogTableRow>) => {
      setSelectedSession(row.original);
      viewSessionDataModal.openModal();
    },
    [viewSessionDataModal],
  );

  const getFilteredPaginatedCatalogs = useCallback(() => {
    return getCatalogs(paginatedFilteredData);
  }, [paginatedFilteredData]);

  const getFilteredPaginatedMappingSessions = useCallback(() => {
    return getMappingSessions<MappingSession>(paginatedFilteredData);
  }, [paginatedFilteredData]);

  const setValues = useCallback(
    (values: CatalogEditValues) => {
      setDefaultFormValues((prev) => ({
        ...prev,
        [selectedTab]: {
          ...prev[selectedTab],
          ...values,
        },
      }));
    },
    [selectedTab],
  );

  const handleAutomatedEdit = useCallback(
    (row: {
      original: {
        title: string;
        version: number;
        id: number;
        catalogItems: CatalogItemsHash;
      };
    }) => {
      setValues({
        templateName: row.original.title || '',
        version: row.original.version || 1.0,
        id: row.original.id,
        ctdSections: createOptionValues(row.original.catalogItems[ItemType.CTDSection]),
        materials: createOptionValues(row.original.catalogItems[ItemType.Material]),
        regions: createOptionValues(row.original.catalogItems[ItemType.Region]),
      });
      catalogEdit.openModal();
    },
    [catalogEdit, setValues],
  );

  const handleCatalogEdit = useCallback(
    (row: {
      original: {
        id: number;
        catalogItems: CatalogItemsHash;
        documentNum: string;
        version: number;
        documentName: string;
      };
    }) => {
      setValues({
        id: row.original.id,
        templateName: row.original?.documentName || '',
        ctdSections: createOptionValues(row.original.catalogItems[ItemType.CTDSection]),
        materials: createOptionValues(row.original.catalogItems[ItemType.Material]),
        processIds: createOptionValues(row.original.catalogItems[ItemType.ProcessID]),
        sites: createOptionValues(row.original.catalogItems[ItemType.Site]),
        dpImages: createOptionValues(row.original.catalogItems[ItemType.DPImage]),
        documentNum: row.original.documentNum,
        version: row.original.version,
        regions: createOptionValues(row.original.catalogItems[ItemType.Region]),
      });
      catalogEdit.openModal();
    },
    [catalogEdit, setValues],
  );

  const handleInProgressEdit = useCallback(
    (row: { original: MappingSession }) => {
      setValues({
        title: row.original.title || '',
        project: row.original.project
          ? [
              {
                label: row.original.project.name,
                value: row.original.project.id.toString(),
              },
            ]
          : [],
        ctdSections: createOptionValues(row.original.catalogItems[ItemType.CTDSection]),
        id: row.original.id,
      });
      catalogEdit.openModal();
    },
    [catalogEdit, setValues],
  );

  const startAuthoringOnClick = useCallback(
    (id: string, useLLM = false) => {
      const parsedSessionId = Number(id);

      navigate('/add-source', {
        state: {
          sessionId: parsedSessionId,
          templateType,
          previousAction: AddSourcesPreviousAction.AUTHORING_REQUEST,
          useLLM,
        },
      });
    },
    [navigate, templateType],
  );

  const handleAuthor = useCallback(
    (id: string) => {
      navigate(`/mapping-sessions/${id}`);
    },
    [navigate],
  );

  const handleVerify = useCallback(
    (id: string) => {
      navigate(`/mapping-sessions/${id}/dv`);
    },
    [navigate],
  );

  const handleConvert = useCallback(
    (id: number, title: string) => {
      convertToAutomatedModal.openModal();
      setSelectedSession({ id, title });
    },
    [convertToAutomatedModal],
  );

  const handleCopy = useCallback(
    (row: Row<CatalogTableRow>) => {
      setSelectedSession(row.original);
      copyDocumentModal.openModal();
    },
    [copyDocumentModal],
  );

  const handleDeleteClick = useCallback(
    (id: string) => {
      setSelectedId(Number(id));
      deleteModal.openModal();
    },
    [deleteModal],
  );

  const handlePreviewData = useCallback(
    (row: CatalogPreviewData) => {
      if (row?.targetFileId) {
        setSelectedAutomatedDocsId(row.targetFileId);
      } else {
        setSelectedAutomatedDocsId(row?.id ?? 0);
      }

      previewTargetAutomatedModal.openModal();
    },
    [previewTargetAutomatedModal],
  );

  const tabValues = useMemo(
    () => ({
      [DocumentTabs.TARGET]: {
        apiCall: getFilteredPaginatedCatalogs,
        deleteApiCall: handleCatalogDelete,
        values: defaultFormValues[selectedTab],
        handleEdit: handleCatalogEdit,
      },
      [DocumentTabs.EXTERNAL]: {
        apiCall: getFilteredPaginatedCatalogs,
        deleteApiCall: handleCatalogDelete,
        values: defaultFormValues[selectedTab],
        handleEdit: handleCatalogEdit,
      },
      [DocumentTabs.AUTOMATED]: {
        apiCall: getFilteredPaginatedMappingSessions,
        deleteApiCall: handleSessionDelete,
        values: defaultFormValues[selectedTab],
        handleEdit: handleAutomatedEdit,
      },
      [DocumentTabs.SOURCE]: {
        apiCall: getFilteredPaginatedCatalogs,
        deleteApiCall: handleCatalogDelete,
        values: defaultFormValues[selectedTab],
        handleEdit: handleCatalogEdit,
      },
      [DocumentTabs.IN_PROGRESS]: {
        apiCall: getFilteredPaginatedMappingSessions,
        deleteApiCall: handleSessionDelete,
        values: defaultFormValues[selectedTab],
        handleEdit: handleInProgressEdit,
      },
      [DocumentTabs.COMPLETED]: {
        apiCall: getFilteredPaginatedMappingSessions,
        deleteApiCall: handleSessionDelete,
        values: defaultFormValues[selectedTab],
        handleEdit: handleAutomatedEdit,
      },
      [DocumentTabs.PRE_AUTHORED]: {
        apiCall: getFilteredPaginatedMappingSessions,
        deleteApiCall: handleSessionDelete,
        values: defaultFormValues[selectedTab],
        handleEdit: handleAutomatedEdit,
      },
    }),
    [
      defaultFormValues,
      selectedTab,
      getFilteredPaginatedCatalogs,
      getFilteredPaginatedMappingSessions,
      handleCatalogDelete,
      handleSessionDelete,
      handleCatalogEdit,
      handleAutomatedEdit,
      handleInProgressEdit,
    ],
  );

  const menuItems = useMemo(
    () =>
      generateMenuItems(
        activeTab,
        selectedTab,
        handleDeleteClick,
        handlePreviewDocument,
        tabValues[selectedTab].handleEdit as (row: {
          original: { title: string; version: number; id: number; catalogItems: CatalogItemsHash };
        }) => void,
        startAuthoringOnClick,
        handleAuthor,
        handleVerify,
        handleConvert,
        handleCopy,
        viewSessionData,
        handlePreviewData,
      ),
    [
      activeTab,
      selectedTab,
      handleDeleteClick,
      handlePreviewDocument,
      tabValues,
      startAuthoringOnClick,
      handleAuthor,
      handleVerify,
      handleConvert,
      handleCopy,
      viewSessionData,
      handlePreviewData,
    ],
  );

  // useEffect(() => {
  // TODO - Fix Params
  // updateParams({ basePath: 'catalog-list', filters: paginatedFilteredData.filters, activeTab });
  // }, [activeTab, paginatedFilteredData.filters]);

  const getMappedOptions = useCallback(
    (items: MenuItemType<CatalogTableRow>[], isConvertDisabled: boolean) => {
      return getDropdownOptions(items).map((item) => {
        if (item.value.title === 'Convert') {
          return {
            ...item,
            disabled: isConvertDisabled,
          };
        }
        return item;
      });
    },
    [],
  );

  const renderDropdown = ({ row }: { row: Row<CatalogTableRow> }) => {
    const isConvertDisabled = disableConvert(activeTab, row.original.status);

    const options = getMappedOptions(menuItems, isConvertDisabled);

    return (
      <div className="space-x-2">
        <Dropdown
          options={options}
          type="button"
          placeholder={<SvgIcon iconType="button-dots" />}
          position="center"
          renderOption={(option) => {
            if (!option) return null;

            const { title, onClick, className } = option.value;

            return (
              <span
                className={twMerge(
                  `py-2 px-4 ${className} ${
                    option.disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`,
                )}
                onClick={option.disabled ? () => undefined : () => onClick(row.original.id, row)}>
                {title}
              </span>
            );
          }}
          customStyles={{
            placeholder: {
              padding: '0.125rem 0.75rem',
            },
            // TODO: remove this after converting all tables to new design
            itemsWrapper: { zIndex: 12 },
            item: {
              display: 'flex',
              justifyContent: 'center',
              padding: '0',
            },
          }}
        />
      </div>
    );
  };

  return (
    <>
      <PaginatedTable
        additionalColumns={
          activeTabColumns as unknown as PaginatedTableProps<BaseItem>['additionalColumns']
        }
        paginatedFilteredData={paginatedFilteredData}
        getData={
          tabValues[selectedTab].apiCall as unknown as PaginatedTableProps<BaseItem>['getData']
        }
        menuItems={menuItems as unknown as MenuItemType<BaseItem>[]}
        handleSelectAll={handleSelectAll}
        onSelect={handleSelect}
        selectedData={selectedRecords}
        handlePageChange={handlePageChange}
        handlePerPageChange={handlePerPageChange}
        queryKey={['Catalog', activeTab, templateType]}
        actionsColumn
        renderDropdown={
          renderDropdown as unknown as NonNullable<PaginatedTableProps<BaseItem>['renderDropdown']>
        }
        refetchInterval={
          activeTab === DocumentTabs.IN_PROGRESS || hasProcessingItems
            ? PROCESSING_POLL_INTERVAL_MS
            : false
        }
        onDataFetched={handleDataFetched as unknown as (data: PaginationResponse<BaseItem>) => void}
      />
      <DeleteConfirmationDialogue
        isOpen={deleteModal.show}
        handleClose={closeDeleteModal}
        handleDelete={tabValues[selectedTab].deleteApiCall}
      />
      {activeTab === DocumentTabs.SOURCE && previewModal.show && (
        <TinyMceModal
          closeEditorModalOpen={previewModal.closeModal}
          editorModalOpen={previewModal.show}
          key="catalog_preview"
          editorType="Source"
          catalogId={selectedId}
          isFileInteractive={isDocInteractive}
        />
      )}
      {catalogEdit.show && (
        <EditCatalogTemplates
          isOpen={catalogEdit.show}
          onClose={catalogEdit.closeModal}
          activeTab={selectedTab}
          defaultValues={tabValues[selectedTab].values}
        />
      )}
      {copyDocumentModal.show && (
        <CopyDocumentSlideOver
          isOpen={copyDocumentModal.show}
          sessionData={selectedSession as MappingSessionRecord}
          handleClose={copyDocumentModal.closeModal}
          onStartAuthoring={handleAuthor}
        />
      )}
      {viewSessionDataModal.show && selectedSession?.id && (
        <ViewSessionDataSlideOver
          isOpen={viewSessionDataModal.show}
          sessionId={selectedSession.id}
          handleClose={viewSessionDataModal.closeModal}
        />
      )}
      {activeTab === DocumentTabs.IN_PROGRESS && convertToAutomatedModal.show && (
        <ConvertToAutoMappingModal
          show={convertToAutomatedModal.show}
          handleClose={convertToAutomatedModal.closeModal}
          mappingSessionData={selectedSession as MappingSessionRecord}
        />
      )}
      {previewTargetAutomatedModal.show && selectedAutomatedDocsId && (
        <TinyMceModal
          closeEditorModalOpen={previewTargetAutomatedModal.closeModal}
          editorModalOpen={previewTargetAutomatedModal.show}
          key="catalog_preview"
          editorType="Session"
          catalogId={selectedAutomatedDocsId}
          isFileInteractive={true}
        />
      )}
    </>
  );
};

export default CatalogTable;
